import React, { useMemo, useState } from "react";
import { Box, Stack, Chip, CircularProgress, LinearProgress, TextField } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import { tokens } from "../../theme";
import { uploadFile, describeUploadLimits } from "../../services/upload";
import { getChecklistDocumentUrl } from "../../services/onboarding";
import { useNotify } from "../NotificationProvider";

import { pressable } from "../pressableProps";
/**
 * An onboarding checklist, shown to whoever is looking at it.
 *
 * The same list serves two readers, so it says who owes what rather than
 * pretending everything is the viewer's to do: an employee opening this sees
 * their five items among the organisation's eleven and can tell at a glance
 * which are theirs. Ticking is offered only where the viewer may actually act —
 * the server enforces it either way, and offering a control that will be
 * refused is worse than not offering it.
 */

const OWNER_LABEL = { employee: "You", admin: "HR", it: "IT", manager: "Manager" };

const dateLabel = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : null;

const isOverdue = (item) =>
  item.status === "pending" && item.dueDate && new Date(item.dueDate) < new Date();

const ChecklistView = ({
  checklist,
  progress,
  // who is looking: "self" | "admin" | "manager"
  viewerRole = "self",
  onUpdate,
  busyItemId,
}) => {
  const [waiveFor, setWaiveFor] = useState(null);
  const notify = useNotify();

  /*
    The stored link points into a private bucket and opened nothing — every
    "View document" here answered 403. The server signs a short-lived read
    instead. The tab is opened on the click and pointed at the file once the
    link arrives: opened after the request, Safari treats it as a pop-up.
  */
  const openDocument = async (item) => {
    const tab = window.open("", "_blank");
    const res = await getChecklistDocumentUrl(checklist?._id, item._id).catch(() => null);
    const url = res?.data?.url;
    if (url && tab) tab.location.href = url;
    else {
      tab?.close();
      notify.error(res?.message || "This document could not be opened.");
    }
  };
  const [reason, setReason] = useState("");
  const [uploadFor, setUploadFor] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const items = useMemo(
    () => [...(checklist?.items || [])].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [checklist]
  );

  if (!checklist) return null;

  // Uploaded but not yet verified: HR verifies every document, so the item
  // stays open until they do. Nobody ticks it — the employee may only swap
  // the file, and an admin decides it from the verification queue.
  const awaitingHR = (item) => item.kind === "document" && item.status === "pending" && Boolean(item.documentUrl);
  const returned = (item) => item.status === "pending" && /^Returned by HR:/.test(item.note || "");

  const mayComplete = (item) => {
    if (item.status !== "pending") return false;
    if (awaitingHR(item)) return false;
    if (viewerRole === "admin") return true;
    if (viewerRole === "self") return item.owner === "employee";
    if (viewerRole === "manager") return item.owner === "manager";
    return false;
  };

  const complete = (item) => {
    // A document item is settled by a document — and, for anyone but an
    // admin, only by HR verifying it — so this asks for the file.
    if (item.kind === "document" && (!item.documentUrl || viewerRole !== "admin")) {
      setUploadFor(item._id);
      setUploadError("");
      return;
    }
    onUpdate?.(item, { status: "done" });
  };

  // The file goes straight to S3; only the resulting address reaches us, and
  // the item is settled in the same gesture — asking somebody to upload and
  // then separately tick would be two steps for one intention.
  const onFile = async (item, file) => {
    if (!file) return;
    setUploading(true);
    setUploadError("");
    const res = await uploadFile(file);
    setUploading(false);
    if (!res.success) {
      setUploadError(res.message);
      return;
    }
    setUploadFor(null);
    // "done" is what is asked for; for an employee the server records the
    // document as submitted and leaves the item waiting for HR.
    onUpdate?.(item, { status: "done", documentUrl: res.url });
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: "0.5rem" }}>
        <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted }}>
          {checklist.templateName || "Onboarding"} ·{" "}
          {progress?.requiredSettled ?? 0} of {progress?.requiredTotal ?? 0} required steps
        </Box>
        <Box sx={{ fontSize: "0.875rem", fontWeight: 600, color: tokens.text }}>
          {progress?.percent ?? 0}%
        </Box>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={progress?.percent ?? 0}
        sx={{
          height: 6,
          borderRadius: 3,
          mb: "1.25rem",
          backgroundColor: tokens.surfaceSubtle,
          "& .MuiLinearProgress-bar": { backgroundColor: tokens.accent, borderRadius: 3 },
        }}
      />

      {items.map((item) => {
        const settled = item.status !== "pending";
        const overdue = isOverdue(item);
        return (
          <Box
            key={item._id}
            sx={{
              display: "flex",
              gap: "1rem",
              padding: "1rem 0",
              borderBottom: `1px solid ${tokens.border}`,
              "&:last-of-type": { borderBottom: "none" },
              opacity: item.status === "waived" ? 0.65 : 1,
            }}
          >
            <Box
              {...pressable(() => mayComplete(item) && !busyItemId && complete(item))}
              sx={{
                color:
                  item.status === "done"
                    ? tokens.success
                    : item.status === "waived"
                    ? tokens.textMuted
                    : overdue
                    ? tokens.danger
                    : tokens.textMuted,
                cursor: mayComplete(item) ? "pointer" : "default",
                display: "flex",
                marginTop: "0.25rem",
              }}
            >
              {busyItemId === item._id ? (
                <CircularProgress size={18} sx={{ color: tokens.textMuted }} />
              ) : item.status === "done" ? (
                <CheckCircleOutlineIcon fontSize="small" />
              ) : item.status === "waived" ? (
                <BlockOutlinedIcon fontSize="small" />
              ) : (
                <RadioButtonUncheckedIcon fontSize="small" />
              )}
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: "wrap", rowGap: "0.25rem" }}>
                <Box
                  sx={{
                    fontSize: "0.9375rem",
                    fontWeight: settled ? 500 : 600,
                    color: tokens.text,
                    textDecoration: item.status === "waived" ? "line-through" : "none",
                  }}
                >
                  {item.label}
                </Box>
                {item.kind === "document" && (
                  <DescriptionOutlinedIcon sx={{ fontSize: "0.9rem", color: tokens.textMuted }} />
                )}
                {!item.required && (
                  <Chip label="Optional" size="small" sx={{ height: "1.125rem", fontSize: "0.8125rem" }} />
                )}
                <Chip
                  label={OWNER_LABEL[item.owner] || item.owner}
                  size="small"
                  sx={{
                    height: "1.125rem",
                    fontSize: "0.8125rem",
                    /*
                      Marigold is reserved for the one irreversible action on a
                      screen; whose job an onboarding step is, is not an action
                      at all. The book tints the employee's own steps plum and
                      everyone else's blue, so "yours" reads apart from "theirs"
                      without borrowing the accent.
                    */
                    background: item.owner === "employee" ? tokens.primaryTint : tokens.infoTint,
                    color: item.owner === "employee" ? tokens.primaryHover : tokens.infoText,
                  }}
                />
              </Stack>

              {item.description && (
                <Box sx={{ mt: "0.25rem", fontSize: "0.8125rem", color: tokens.textMuted }}>
                  {item.description}
                </Box>
              )}

              <Stack direction="row" spacing={1.5} sx={{ mt: "0.25rem", flexWrap: "wrap" }}>
                {item.dueDate && item.status === "pending" && (
                  <Box sx={{ fontSize: "0.8125rem", color: overdue ? tokens.danger : tokens.textMuted, fontWeight: overdue ? 700 : 400 }}>
                    {overdue ? "Overdue — due " : "Due "}
                    {dateLabel(item.dueDate)}
                  </Box>
                )}
                {awaitingHR(item) && (
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.warningText, fontWeight: 600 }}>
                    {viewerRole === "admin" ? "Submitted — waiting for you to verify" : "Submitted — HR will verify it"}
                  </Box>
                )}
                {returned(item) && (
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.danger, fontWeight: 600 }}>{item.note}</Box>
                )}
                {item.status === "waived" && item.note && (
                  // the reason is the whole point of waiving; hiding it would
                  // leave a struck-through line nobody can account for later
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Waived — {item.note}</Box>
                )}
                {item.documentUrl && (
                  <Box
                    component="button"
                    type="button"
                    onClick={() => openDocument(item)}
                    sx={{ font: "inherit", fontSize: "0.8125rem", color: tokens.accent, background: "none", border: 0, p: 0, cursor: "pointer", textAlign: "left" }}
                  >
                    View document
                  </Box>
                )}
                {awaitingHR(item) && viewerRole === "admin" && (
                  <Box
                    component="a"
                    href="/app/employees/documents"
                    sx={{ fontSize: "0.8125rem", color: tokens.accent, textDecoration: "none" }}
                  >
                    Verify in Documents
                  </Box>
                )}
                {awaitingHR(item) && viewerRole === "self" && uploadFor !== item._id && (
                  <Box
                    {...pressable(() => { setUploadFor(item._id); setUploadError(""); })}
                    sx={{ fontSize: "0.8125rem", color: tokens.accent, cursor: "pointer" }}
                  >
                    Replace file
                  </Box>
                )}
                {viewerRole === "admin" && item.status === "pending" && (
                  <Box
                    {...pressable(() => { setWaiveFor(item._id); setReason(""); })}
                    sx={{ fontSize: "0.8125rem", color: tokens.textMuted, cursor: "pointer", textDecoration: "underline" }}
                  >
                    Waive
                  </Box>
                )}
              </Stack>

              {uploadFor === item._id && (
                <Box sx={{ mt: "0.5rem" }}>
                  <Box
                    component="label"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.5rem 1rem",
                      border: `1px dashed ${tokens.border}`,
                      borderRadius: "8px",
                      cursor: uploading ? "default" : "pointer",
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      color: tokens.accent,
                      "&:hover": { background: tokens.surfaceSubtle },
                    }}
                  >
                    {uploading ? (
                      <>
                        <CircularProgress size={14} sx={{ color: tokens.textMuted }} /> Uploading…
                      </>
                    ) : (
                      <>
                        <UploadFileOutlinedIcon fontSize="small" /> Choose a file
                      </>
                    )}
                    <input
                      type="file"
                      hidden
                      disabled={uploading}
                      accept=".pdf,.png,.jpg,.jpeg,.webp,.heic"
                      onChange={(e) => onFile(item, e.target.files?.[0])}
                    />
                  </Box>
                  <Box sx={{ mt: "0.25rem", fontSize: "0.8125rem", color: uploadError ? tokens.danger : tokens.textMuted }}>
                    {uploadError || describeUploadLimits()}
                  </Box>
                </Box>
              )}

              {waiveFor === item._id && (
                <Stack direction="row" spacing={1} sx={{ mt: "0.5rem" }}>
                  <TextField
                    size="small"
                    fullWidth
                    autoFocus
                    placeholder="Why does this not apply?"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <Box
                    {...pressable(() => {
                      if (!reason.trim()) return;
                      onUpdate?.(item, { status: "waived", note: reason.trim() });
                      setWaiveFor(null);
                    })}
                    sx={{ alignSelf: "center", fontSize: "0.8125rem", fontWeight: 600, color: tokens.accent, cursor: "pointer" }}
                  >
                    Waive
                  </Box>
                </Stack>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ChecklistView;
